import React from 'react';
import styled from 'styled-components';
import SecureImage from './SecureImage';
import Barcode from 'react-barcode';
import StatusIndicator from './StatusIndicator';
import { formatWeightForDisplay } from '../utils/weightString';
import { resolveCardStatus } from '../utils/pidStatus';
import { commodityColor as colorForCommodity } from '../utils/commodityColor';

const CardWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background: var(--white-a15);
  box-shadow: 0 4px 10px var(--shadow-a08);
  backdrop-filter: blur(12px);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 18px var(--shadow-a08);
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoSection = styled.div`
  padding: 0.75rem 1rem;
  color: var(--black);
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
  background: var(--white-a40);
`;

/* ---------- PLU header row + pill (PLU only) ---------- */
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;     /* keeps the row centered like the old layout */
  gap: 0.5rem;
  flex-wrap: wrap;
  flex-direction: column;
`;

const TitleText = styled.span`
  display: inline-block;
  min-width: 0;                /* allow ellipsis to work in flex */
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* pill */
const CommodityPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--white);
  background: ${({ $bg }) => $bg};
  border: 2px solid ${({ $border }) => $border};
  box-shadow: 0 2px 6px var(--shadow-a15);
  user-select: none;
  max-width: 100%;
`;

/* Commodity palette lives in utils/commodityColor.js (imported above as
   colorForCommodity) so every commodity pill in the app shares one source. */

const ItemCard = ({ group, activeTab, layout, onClick }) => {
  let imageSrc = group.Picture;
  let folder = '';

  if (activeTab === 'Methods') {
    folder = 'PIDMethods';
    if (!imageSrc) imageSrc = 'MethodFallback.webp';
  } else if (activeTab === 'Bags') {
    folder = 'PIDBags';
    if (!imageSrc) imageSrc = 'BagFallback.webp';
  } else if (activeTab === 'PLU') {
    folder = 'PIDPLU';
    if (!imageSrc) imageSrc = 'https://thevaultmedia.blob.core.windows.net/vault-images/PIDPLU/PLUFallback.webp';
  } else if (activeTab === 'Displays') {
    folder = 'PIDDisplays';
    if (!imageSrc) imageSrc = 'https://thevaultmedia.blob.core.windows.net/vault-images/PIDDisplays/DisplayFallback.webp';
  } else if (activeTab === 'UPC') {
    folder = 'PIDUPC';
    imageSrc = ''; // will render barcode instead of image
  }

  const displayWeight = activeTab === 'UPC' ? formatWeightForDisplay(group.Weight) : '';

  const title =
    activeTab === 'Methods' ? group.MethodName :
    activeTab === 'Bags' ? group.LabelID :
    activeTab === 'PLU' ? group.LabelID :
    activeTab === 'Displays' ? (group.ProductID || group.Description || '') :
    // UPC card title bottom line: Retailer + Weight lb
    `${group.Retailer || ''} ${displayWeight}`.trim();

  const imageKey = `${folder}/${imageSrc}`;

  // For PLU: grab commodity from first item
  const commodity =
    activeTab === 'PLU'
      ? (group.Items?.[0]?.Commodity || '')
      : activeTab === 'UPC'
      ? (group.Commodity || '')
      : '';

  const pillColors = (activeTab === 'PLU' || activeTab === 'UPC') ? colorForCommodity(commodity) : null;

  // Lifecycle status for the browse indicator (StatusIndicator renders nothing for Active).
  const status = resolveCardStatus(group);

  return (
    <CardWrapper onClick={onClick}>
      <ImageWrapper>
        {activeTab === 'UPC' ? (
          <div style={{ width: '90%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {String(group.UPC || '').replace(/\D+/g, '').length === 12 ? (
              <Barcode
                value={String(group.UPC).replace(/\D+/g, '')}
                format={'UPC'}
                height={60}
                width={1.5}
                displayValue
                background="transparent"
                font="OCRB"
              />
            ) : (
              <span>No UPC</span>
            )}
          </div>
        ) : (
          <SecureImage
            key={imageKey}
            src={imageSrc}
            folder={folder}
            alt={title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '0',
            }}
            loading="lazy"
          />
        )}
      </ImageWrapper>

      {activeTab === 'PLU' || activeTab === 'UPC' ? (
        <InfoSection>
          <HeaderRow>
            {commodity ? (
              <CommodityPill $bg={pillColors.bg} $border={pillColors.border}>
                {commodity}
              </CommodityPill>
            ) : null}
            <TitleText title={title}>{title}</TitleText>
            <StatusIndicator status={status} size="sm" />
          </HeaderRow>
        </InfoSection>
      ) : (
        <InfoSection>
          <HeaderRow>
            <span>{title}</span>
            <StatusIndicator status={status} size="sm" />
          </HeaderRow>
        </InfoSection>
      )}
    </CardWrapper>
  );
};

export default React.memo(ItemCard, (prev, next) => {
  return (
    prev.group?.Picture === next.group?.Picture &&
    prev.group?.MethodName === next.group?.MethodName &&
    prev.group?.LabelID === next.group?.LabelID &&
    prev.group?.UniqueID === next.group?.UniqueID &&
    prev.group?.UPC === next.group?.UPC &&
    prev.group?.Retailer === next.group?.Retailer &&
    prev.group?.Weight === next.group?.Weight &&
    resolveCardStatus(prev.group) === resolveCardStatus(next.group) &&
    prev.activeTab === next.activeTab &&
    prev.layout === next.layout
  );
});
